import { useEffect, useState } from "react";
import { Lock, HandCoins, Check, ShieldCheck, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, gbp } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

let cache = null;
const subs = new Set();
const publish = (d) => { cache = d; subs.forEach((f) => f(d)); };

export function usePaymentMode() {
  const [d, setD] = useState(cache);
  useEffect(() => {
    subs.add(setD);
    if (!cache) api.get("/provider/payment-mode").then((r) => publish(r.data)).catch(() => {});
    return () => subs.delete(setD);
  }, []);
  return d;
}

const BANNER_KEY = "mhh_protected_banner_closed";
const PERKS = ["Your money is held safely until you confirm the job is done", "Covered by the £1,000 MyHomelyHub guarantee", "Book & pay online, or pay in stages on bigger jobs", "Help from us if anything goes wrong"];

export function ProtectedBanner({ show, testid = "protected-banner" }) {
  const [closed, setClosed] = useState(() => localStorage.getItem(BANNER_KEY) === "1");
  if (!show || closed) return null;
  const close = () => { localStorage.setItem(BANNER_KEY, "1"); setClosed(true); };
  return (
    <div className="relative rounded-2xl border border-emerald-200 bg-emerald-50 p-4 pr-10 mb-4 animate-in fade-in slide-in-from-top-1 duration-300" data-testid={testid}>
      <button onClick={close} className="absolute top-3 right-3 p-1 rounded-full text-emerald-700 hover:bg-emerald-100 transition-colors" aria-label="Close" data-testid={`${testid}-close`}><X size={16} /></button>
      <div className="flex items-center gap-2 font-bold text-emerald-900"><ShieldCheck size={18} className="text-emerald-600" />Why choose protected payment?</div>
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm text-emerald-900">{PERKS.map((t) => <li key={t} className="flex gap-2"><Check size={15} className="text-emerald-600 shrink-0 mt-0.5" />{t}</li>)}</ul>
      <p className="text-xs text-emerald-800/80 mt-2">These pros take payment through MyHomelyHub. Pros who take payment directly are hidden while this filter is on.</p>
      <a href="/guarantee" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-sm font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-950 transition-colors" data-testid={`${testid}-guarantee-link`}>How the £1,000 guarantee works<ExternalLink size={13} /></a>
    </div>
  );
}

export function PaymentLabel({ mode, testid = "payment-label", small }) {
  const direct = mode === "direct";
  const cls = direct ? "bg-slate-100 text-slate-700 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-semibold whitespace-nowrap ${small ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5"} ${cls}`}
      title={direct ? "You pay this professional directly. Payments aren't held by MyHomelyHub and the £1,000 guarantee doesn't apply." : "You pay through MyHomelyHub. Your money is held safely until the job is done and covered by our £1,000 guarantee."}
      data-testid={testid}>{direct ? <HandCoins size={11} /> : <Lock size={11} />}{direct ? "Pays the pro directly" : "Protected payment"}</span>
  );
}

function Option({ mode, active, onPick, title, children, testid }) {
  return (
    <button type="button" onClick={() => onPick(mode)} className={`text-left rounded-xl border-2 p-4 transition-colors ${active ? "border-[#FF8166] bg-[#FFF1EE]/50" : "border-slate-200 hover:border-slate-300"}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2"><b className="text-slate-900">{title}</b>{active && <Check size={16} className="text-[#FF8166]" />}</div>
      <div className="text-xs text-slate-600 mt-1.5 space-y-1">{children}</div>
    </button>
  );
}

function Choices({ d, pick, setPick }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Option mode="platform" active={pick === "platform"} onPick={setPick} title="Through MyHomelyHub" testid="payment-mode-platform">
        <p className="font-semibold text-emerald-700">Save {d.saving}% on all your fees</p>
        <p className="font-semibold text-emerald-700" data-testid="payment-mode-boost-perk">Small boost in search results</p>
        <p>Commission is taken per job. Homeowners pay safely online, with Book & pay, stage payments, tips, gift vouchers and our £1,000 guarantee.</p>
      </Option>
      <Option mode="direct" active={pick === "direct"} onPick={setPick} title="I'll take payments myself" testid="payment-mode-direct">
        <p className="font-semibold text-slate-800">+{d.uplift}% on subscriptions, credits, featured and Priority Leads</p>
        <p>No commission. Homeowners can still book online and pay you on the day, but stage payments, tips, vouchers and the guarantee aren't available.</p>
      </Option>
    </div>
  );
}

function useSave(onDone) {
  const [busy, setBusy] = useState(false);
  const save = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/provider/payment-mode", { mode }); publish(data);
      toast.success(data.rewarded > 0 ? `Switched. ${data.rewarded} free credits added to your account` : mode === "direct" ? "You'll take payments directly" : "You'll take payments through MyHomelyHub"); onDone?.();
    }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return [busy, save];
}

export function FeeExamples({ e: given }) {
  const [e, setE] = useState(given || null);
  useEffect(() => { if (!given) api.get("/payment-mode/example").then((r) => setE(r.data)).catch(() => {}); }, [given]);
  if (!e) return null;
  const m = 1 + e.uplift / 100;
  const monthly = (e.plan?.monthly_price || 0) + e.pack.price;
  const com = (e.job_value * e.commission) / 100;
  const Row = ({ k, v, testid }) => <div className="flex justify-between gap-2 mt-1"><span className="text-slate-600">{k}</span><b className="text-slate-900" data-testid={testid}>{v}</b></div>;
  return (
    <div className="grid sm:grid-cols-2 gap-3 text-xs" data-testid="fee-examples">
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="fee-example-monthly">
        <b className="text-slate-900 text-sm">Example: monthly fees</b>
        <p className="muted">{e.plan ? `${e.plan.name} plan + ` : ""}{e.pack.credits} credits</p>
        <Row k="Through MyHomelyHub" v={gbp(monthly)} testid="fee-example-monthly-platform" />
        <Row k="Taking payments yourself" v={gbp(monthly * m)} testid="fee-example-monthly-direct" />
        <p className="text-emerald-700 font-semibold mt-2">Save {gbp(monthly * m - monthly)} a month on fees with MyHomelyHub payments</p>
      </div>
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="fee-example-job">
        <b className="text-slate-900 text-sm">Example: a {gbp(e.job_value)} job</b>
        <p className="muted">What you pay MyHomelyHub on the job itself</p>
        <Row k="Through MyHomelyHub" v={`${gbp(com)} (${e.commission}% commission)`} testid="fee-example-job-platform" />
        <Row k="Taking payments yourself" v={`${gbp(0)} commission`} testid="fee-example-job-direct" />
        <p className="text-slate-600 mt-2">Pros who take payments directly pay {e.uplift}% more on all fees instead, and their customers don't get escrow or the £1,000 guarantee.</p>
      </div>
    </div>
  );
}

export function FeeCalculator({ testid = "fee-calculator" }) {
  const [e, setE] = useState(null);
  const [v, setV] = useState({ jobs: 4, avg: 250, fees: "" });
  useEffect(() => { api.get("/payment-mode/example").then((r) => { setE(r.data); setV((x) => ({ ...x, fees: x.fees === "" ? String((r.data.plan?.monthly_price || 0) + r.data.pack.price) : x.fees })); }).catch(() => {}); }, []);
  if (!e) return null;
  const fees = +v.fees || 0, jobs = Math.max(0, +v.jobs || 0), avg = Math.max(0, +v.avg || 0);
  const commission = (jobs * avg * e.commission) / 100;
  const platform = fees + commission, direct = fees * (1 + e.uplift / 100);
  const diff = Math.abs(platform - direct);
  const better = platform < direct ? "platform" : direct < platform ? "direct" : "same";
  const inp = (k, label, pre) => (
    <label className="text-xs font-semibold text-slate-700">{label}
      <div className="relative mt-1">{pre && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{pre}</span>}
        <input type="number" min="0" inputMode="decimal" className={`input py-2 ${pre ? "pl-7" : ""}`} value={v[k]} onChange={(ev) => setV({ ...v, [k]: ev.target.value })} data-testid={`${testid}-${k}`} /></div></label>);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <b className="text-sm text-slate-900">Fee calculator: which option saves you more?</b>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">{inp("jobs", "Jobs a month")}{inp("avg", "Average job value", "£")}{inp("fees", "Monthly fees (plan, credits, featured)", "£")}</div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
        <div className={`rounded-lg p-3 border ${better === "platform" ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}><div className="text-xs muted">Through MyHomelyHub</div><b className="text-lg" data-testid={`${testid}-platform`}>{gbp(platform)}</b><div className="text-xs muted">{gbp(fees)} fees + {gbp(commission)} commission ({e.commission}%)</div></div>
        <div className={`rounded-lg p-3 border ${better === "direct" ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}><div className="text-xs muted">Taking payments yourself</div><b className="text-lg" data-testid={`${testid}-direct`}>{gbp(direct)}</b><div className="text-xs muted">{gbp(fees)} fees + {e.uplift}% uplift, no commission</div></div>
      </div>
      <p className="text-sm font-semibold mt-3" data-testid={`${testid}-result`}>{better === "same" ? "Both options cost the same." : better === "platform" ? `MyHomelyHub payments save you ${gbp(diff)} a month.` : `Taking payments yourself saves you ${gbp(diff)} a month.`}</p>
      <p className="text-xs muted mt-1">Monthly estimate. MyHomelyHub payments also give your customers escrow protection, Book & pay and the £1,000 guarantee.</p>
    </div>
  );
}

export function SignupPaymentChoice({ value, onChange }) {
  const [e, setE] = useState(null);
  useEffect(() => { api.get("/payment-mode/example").then((r) => setE(r.data)).catch(() => {}); }, []);
  if (!e) return null;
  return (
    <div className="space-y-3 pt-1" data-testid="signup-payment-choice">
      <div><b className="text-sm text-slate-900">How will you take payments?</b><p className="text-xs muted">You can change this any time in Billing.</p></div>
      <Choices d={e} pick={value} setPick={onChange} />
      <FeeExamples e={e} />
      <details className="group"><summary className="text-xs font-semibold text-[#FF8166] cursor-pointer" data-testid="signup-fee-calculator-toggle">Work out your own numbers</summary><div className="mt-2"><FeeCalculator testid="signup-fee-calculator" /></div></details>
    </div>
  );
}

export function PaymentModePrompt() {
  const d = usePaymentMode();
  const [pick, setPick] = useState("platform");
  const [busy, save] = useSave();
  if (!d || d.mode) return null;
  return (
    <Dialog open>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="payment-mode-prompt">
        <DialogHeader><DialogTitle>How would you like to take payments?</DialogTitle><DialogDescription>You can change this any time in Billing.</DialogDescription></DialogHeader>
        <Choices d={d} pick={pick} setPick={setPick} />
        <FeeExamples />
        <button disabled={busy} onClick={() => save(pick)} className="btn-coral w-full" data-testid="payment-mode-save">Continue</button>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentModeCard({ onChanged }) {
  const d = usePaymentMode();
  const [pick, setPick] = useState(null);
  const [busy, save] = useSave(onChanged);
  if (!d) return null;
  const cur = pick || d.mode || "platform";
  return (
    <section className="card p-5" data-testid="payment-mode-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3"><h2 className="font-bold">How you take payments</h2><PaymentLabel mode={d.mode} testid="payment-mode-current" /></div>
      {d.mode === "direct" && <p className="text-sm mb-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 p-3" data-testid="payment-mode-uplift-note">Your fees include a {d.uplift}% direct-payments uplift. Take payments through MyHomelyHub and save {d.saving}% on every fee.</p>}
      {d.reward && <p className="text-sm mb-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 p-3" data-testid="payment-mode-reward-note">{d.reward.eligible ? `Switch to MyHomelyHub payments now and get ${d.reward.credits} free credits (one-off).` : `Switch to MyHomelyHub payments after ${d.reward.days_left} more day${d.reward.days_left === 1 ? "" : "s"} and get ${d.reward.credits} free credits (one-off, for pros who have taken payments directly for ${d.reward.days} days).`}</p>}
      <Choices d={d} pick={cur} setPick={setPick} />
      {cur !== d.mode && <button disabled={busy} onClick={() => save(cur)} className="btn-coral btn-sm mt-3" data-testid="payment-mode-switch-btn">{cur === "direct" ? "Switch to taking payments myself" : "Switch to MyHomelyHub payments"}</button>}
      <p className="text-xs muted mt-2">New prices apply to new purchases straight away and to subscriptions from their next renewal.</p>
      <div className="mt-4"><FeeCalculator testid="billing-fee-calculator" /></div>
    </section>
  );
}
